import { useState, useEffect, useCallback } from 'react';
import useSWR from 'swr';
import TabPageLayout, { PageLoader, DataCard, StatCard, EmptyState } from './TabPageLayout';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { 
  Clock, Play, Square, Users, DollarSign, Calendar, 
  CheckCircle2, XCircle, Loader2, RefreshCw, Download,
  AlertCircle, History, User, ChevronDown, ChevronUp,
  Banknote, Calculator, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

// Format duration in hours and minutes
const formatDuration = (minutes) => {
  if (!minutes || minutes <= 0) return '0h 0m';
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return `${hours}h ${mins}m`;
};

// Format currency
const formatCurrency = (amount, currency = 'USD') => {
  const symbols = { USD: '$', EUR: '€', GBP: '£', AED: 'AED ', AUD: 'A$', CAD: 'C$' };
  const symbol = symbols[currency] || '$';
  return `${symbol}${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Format date/time
const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', { 
    month: 'short', day: 'numeric', 
    hour: 'numeric', minute: '2-digit', 
    hour12: true 
  });
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const TeamPayTab = () => {
  // Get current user info
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const currentUserId = userData.id || userData.userId;
  const userRole = userData.role;
  const isManager = userRole === 'admin' || userRole === 'owner' || userRole === 'manager';
  
  // State
  const [activeView, setActiveView] = useState('timeclock'); // timeclock, payroll
  const [selectedPeriod, setSelectedPeriod] = useState('week'); // week, biweekly, month
  const [clockingIn, setClockingIn] = useState(false);
  const [clockingOut, setClockingOut] = useState(false);
  const [showPayrollModal, setShowPayrollModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [markingPaid, setMarkingPaid] = useState(false);
  const [expandedMember, setExpandedMember] = useState(null);
  
  // Currency
  const orgCountry = localStorage.getItem('orgCountry') || 'USA';
  const currency = orgCountry === 'UAE' ? 'AED' : 'USD';

  // Calculate period dates
  const getPeriodDates = useCallback(() => {
    const now = new Date();
    let startDate, endDate;
    
    if (selectedPeriod === 'week') {
      // Current week (Monday to Sunday)
      const dayOfWeek = now.getDay();
      const monday = new Date(now);
      monday.setDate(now.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
      monday.setHours(0, 0, 0, 0);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      sunday.setHours(23, 59, 59, 999);
      startDate = monday;
      endDate = sunday;
    } else if (selectedPeriod === 'biweekly') {
      // Last 14 days
      startDate = new Date(now);
      startDate.setDate(now.getDate() - 14);
      startDate.setHours(0, 0, 0, 0);
      endDate = now;
    } else {
      // Current month
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    }
    
    return {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    };
  }, [selectedPeriod]);

  // Fetcher
  const fetcher = async (url) => {
    const res = await api.get(url);
    return res.data;
  };

  // Fetch team members
  const { data: teamData, error: teamError, isLoading: teamLoading, mutate: mutateTeam } = useSWR(
    '/team',
    fetcher,
    { revalidateOnFocus: true, refreshInterval: 30000 } // Refresh every 30s
  );

  // Fetch time entries for the period
  const { start, end } = getPeriodDates();
  const { data: timeEntriesData, error: entriesError, isLoading: entriesLoading, mutate: mutateEntries } = useSWR(
    `/timeclock/entries?start_date=${start}&end_date=${end}`,
    fetcher,
    { revalidateOnFocus: true, refreshInterval: 30000 }
  );

  // Fetch current user's clock status - use /status for current user or /status/{id} for specific user
  const { data: clockStatus, mutate: mutateClockStatus } = useSWR(
    currentUserId ? `/timeclock/status/${currentUserId}` : '/timeclock/status',
    fetcher,
    { revalidateOnFocus: true, refreshInterval: 10000 } // Check every 10s
  );

  const team = teamData || [];
  const timeEntries = timeEntriesData?.entries || timeEntriesData || [];
  const isClockedIn = clockStatus?.isClockedIn || false;
  // currentEntry format: { id, clockIn, teamMemberId }
  const currentEntry = clockStatus?.currentEntry || clockStatus?.openShift || null;

  // Calculate hours for each team member
  const calculateMemberHours = (memberId) => {
    const memberEntries = timeEntries.filter(e => e.teamMemberId === memberId);
    let totalMinutes = 0;
    let paidMinutes = 0;
    let unpaidMinutes = 0;
    
    memberEntries.forEach(entry => {
      if (entry.clockOut) {
        const clockIn = new Date(entry.clockIn);
        const clockOut = new Date(entry.clockOut);
        const minutes = (clockOut - clockIn) / (1000 * 60);
        totalMinutes += minutes;
        
        if (entry.isPaid) {
          paidMinutes += minutes;
        } else {
          unpaidMinutes += minutes;
        }
      }
    });
    
    return { totalMinutes, paidMinutes, unpaidMinutes, entries: memberEntries };
  };

  // Get team summary
  const getTeamSummary = () => {
    let totalHours = 0;
    let unpaidHours = 0;
    let clockedInCount = 0;
    
    team.forEach(member => {
      const { totalMinutes, unpaidMinutes } = calculateMemberHours(member.id);
      totalHours += totalMinutes / 60;
      unpaidHours += unpaidMinutes / 60;
      
      // Check if currently clocked in
      const hasOpenEntry = timeEntries.some(e => e.teamMemberId === member.id && !e.clockOut);
      if (hasOpenEntry) clockedInCount++;
    });
    
    return { totalHours, unpaidHours, clockedInCount };
  };

  const summary = getTeamSummary();

  // Clock In
  const handleClockIn = async () => {
    setClockingIn(true);
    try {
      // Backend expects ClockInRequest format - simple call without location for non-geo mode
      await api.post('/timeclock/clock-in', {
        source: 'web',
        note: ''
      });
      toast.success('Clocked in successfully!');
      mutateClockStatus();
      mutateEntries();
    } catch (error) {
      console.error('Clock in error:', error);
      toast.error(error.response?.data?.detail || 'Failed to clock in');
    } finally {
      setClockingIn(false);
    }
  };

  // Clock Out
  const handleClockOut = async () => {
    setClockingOut(true);
    try {
      // Backend expects ClockOutRequest format
      await api.post('/timeclock/clock-out', {
        source: 'web',
        note: ''
      });
      toast.success('Clocked out successfully!');
      mutateClockStatus();
      mutateEntries();
    } catch (error) {
      console.error('Clock out error:', error);
      toast.error(error.response?.data?.detail || 'Failed to clock out');
    } finally {
      setClockingOut(false);
    }
  };

  // Mark entries as paid
  const handleMarkPaid = async () => {
    if (!selectedMember) return;
    
    setMarkingPaid(true);
    try {
      const { entries } = calculateMemberHours(selectedMember.id);
      const unpaidEntries = entries.filter(e => !e.isPaid && e.clockOut);
      
      // Mark each entry as paid
      await Promise.all(unpaidEntries.map(entry => 
        api.patch(`/timeclock/entries/${entry.id}`, { isPaid: true, paidAmount: parseFloat(payAmount) || 0 })
      ));
      
      toast.success(`Marked ${unpaidEntries.length} entries as paid`);
      setShowPayrollModal(false);
      setSelectedMember(null);
      setPayAmount('');
      mutateEntries();
    } catch (error) {
      console.error('Mark paid error:', error);
      toast.error('Failed to mark as paid');
    } finally {
      setMarkingPaid(false);
    }
  };

  // Export timesheet
  const handleExport = async () => {
    try {
      const response = await api.get(`/timeclock/export?start_date=${start}&end_date=${end}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `timesheet_${start}_${end}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Timesheet exported!');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  // Get period label
  const getPeriodLabel = () => {
    const { start, end } = getPeriodDates();
    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  // Loading
  const isLoading = teamLoading || entriesLoading;

  if (isLoading) {
    return (
      <TabPageLayout title="Team Pay">
        <PageLoader />
      </TabPageLayout>
    );
  }

  // Find current user in team
  const currentTeamMember = team.find(m => m.id === currentUserId || m.userId === currentUserId);

  return (
    <TabPageLayout
      title="Team Pay"
      subtitle="Track hours and manage payroll"
      actions={
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { mutateTeam(); mutateEntries(); mutateClockStatus(); }}
            className="h-9"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          {isManager && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              className="h-9"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-6">
        {/* Clock In/Out Card - Always visible for team members */}
        <DataCard>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-2">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                isClockedIn ? 'bg-green-100' : 'bg-gray-100'
              }`}>
                <Clock className={`w-7 h-7 ${isClockedIn ? 'text-green-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">
                  {currentTeamMember?.name || userData.name || 'Your Status'}
                </h3>
                {isClockedIn ? (
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-100 text-green-700">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Clocked In
                    </Badge>
                    {currentEntry && (
                      <span className="text-sm text-gray-500">
                        since {formatDateTime(currentEntry.clockIn || currentEntry.clockInTimeUTC)}
                      </span>
                    )}
                  </div>
                ) : (
                  <Badge variant="outline" className="text-gray-500">
                    <XCircle className="w-3 h-3 mr-1" />
                    Clocked Out
                  </Badge>
                )}
              </div>
            </div>
            
            <div className="flex gap-3">
              {isClockedIn ? (
                <Button
                  onClick={handleClockOut}
                  disabled={clockingOut}
                  className="bg-red-600 hover:bg-red-700 text-white h-12 px-6"
                >
                  {clockingOut ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Square className="w-5 h-5 mr-2" />
                  )}
                  Clock Out
                </Button>
              ) : (
                <Button
                  onClick={handleClockIn}
                  disabled={clockingIn}
                  className="bg-green-600 hover:bg-green-700 text-white h-12 px-6"
                >
                  {clockingIn ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Play className="w-5 h-5 mr-2" />
                  )}
                  Clock In
                </Button>
              )}
            </div>
          </div>
        </DataCard>

        {/* Period Selector */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-gray-200 overflow-hidden">
              {['week', 'biweekly', 'month'].map((period) => (
                <button
                  key={period}
                  onClick={() => setSelectedPeriod(period)}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    selectedPeriod === period
                      ? 'bg-gray-900 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  } ${period !== 'week' ? 'border-l border-gray-200' : ''}`}
                >
                  {period === 'week' ? 'Week' : period === 'biweekly' ? '2 Weeks' : 'Month'}
                </button>
              ))}
            </div>
          </div>
          <span className="text-sm text-gray-500">{getPeriodLabel()}</span>
        </div>

        {/* Summary Stats - Manager View */}
        {isManager && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard
              title="Team Members"
              value={team.length}
              icon={<Users className="w-5 h-5 text-blue-600" />}
            />
            <StatCard
              title="Clocked In"
              value={summary.clockedInCount}
              icon={<CheckCircle2 className="w-5 h-5 text-green-600" />}
              subtitle="currently working"
            />
            <StatCard
              title="Total Hours"
              value={summary.totalHours.toFixed(1)}
              icon={<Clock className="w-5 h-5 text-purple-600" />}
              subtitle="this period"
            />
            <StatCard
              title="Unpaid Hours"
              value={summary.unpaidHours.toFixed(1)}
              icon={<DollarSign className="w-5 h-5 text-amber-600" />}
              subtitle="needs payment"
              highlight={summary.unpaidHours > 0}
            />
          </div>
        )}

        {/* View Toggle */}
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            {[
              { id: 'timeclock', label: 'Team Status', icon: Users },
              { id: 'payroll', label: 'Time History', icon: History }
            ].map((view) => {
              const Icon = view.icon;
              return (
                <button
                  key={view.id}
                  onClick={() => setActiveView(view.id)}
                  className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 ${
                    activeView === view.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  } ${view.id !== 'timeclock' ? 'border-l border-gray-200' : ''}`}
                >
                  <Icon className="w-4 h-4" />
                  {view.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Team Status View */}
        {activeView === 'timeclock' && (
          <DataCard title="Team Members" subtitle="Current clock status and hours">
            {team.length === 0 ? (
              <EmptyState
                icon={<Users />}
                title="No team members"
                description="Add team members to track their hours"
              />
            ) : (
              <div className="space-y-3">
                {team.map((member) => {
                  const { totalMinutes, unpaidMinutes, entries } = calculateMemberHours(member.id);
                  const hasOpenEntry = entries.some(e => !e.clockOut);
                  const isExpanded = expandedMember === member.id;
                  
                  return (
                    <div key={member.id} className="border border-gray-200 rounded-xl overflow-hidden">
                      <div 
                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => setExpandedMember(isExpanded ? null : member.id)}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            hasOpenEntry ? 'bg-green-100' : 'bg-gray-100'
                          }`}>
                            <User className={`w-5 h-5 ${hasOpenEntry ? 'text-green-600' : 'text-gray-400'}`} />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{member.name}</p>
                            <div className="flex items-center gap-2">
                              {hasOpenEntry ? (
                                <Badge className="bg-green-100 text-green-700 text-xs">
                                  <CheckCircle2 className="w-3 h-3 mr-1" />
                                  Working
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-gray-500 text-xs">Off</Badge>
                              )}
                              <span className="text-xs text-gray-500">{member.role || 'Team Member'}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-semibold text-gray-900">{formatDuration(totalMinutes)}</p>
                            {unpaidMinutes > 0 && (
                              <p className="text-xs text-amber-600">{formatDuration(unpaidMinutes)} unpaid</p>
                            )}
                          </div>
                          
                          {isManager && unpaidMinutes > 0 && (
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedMember(member);
                                setShowPayrollModal(true);
                              }}
                              className="bg-blue-600 hover:bg-blue-700 text-white h-9"
                            >
                              <Banknote className="w-4 h-4 mr-1" />
                              Pay
                            </Button>
                          )}
                          
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                      
                      {/* Expanded History */}
                      {isExpanded && entries.length > 0 && (
                        <div className="border-t border-gray-200 bg-gray-50 p-4">
                          <p className="text-xs font-medium text-gray-500 mb-3">Recent Time Entries</p>
                          <div className="space-y-2">
                            {entries.slice(0, 5).map((entry) => {
                              const duration = entry.clockOut 
                                ? (new Date(entry.clockOut) - new Date(entry.clockIn)) / (1000 * 60)
                                : null;
                              
                              return (
                                <div key={entry.id} className="flex items-center justify-between text-sm bg-white p-2 rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <div className={`w-2 h-2 rounded-full ${entry.isPaid ? 'bg-green-500' : 'bg-amber-500'}`} />
                                    <span className="text-gray-600">
                                      {formatDateTime(entry.clockIn)}
                                    </span>
                                    {entry.clockOut && (
                                      <>
                                        <span className="text-gray-400">→</span>
                                        <span className="text-gray-600">
                                          {formatDateTime(entry.clockOut)}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {duration ? (
                                      <span className="font-medium">{formatDuration(duration)}</span>
                                    ) : (
                                      <Badge className="bg-green-100 text-green-700 text-xs">Active</Badge>
                                    )}
                                    {entry.isPaid && (
                                      <Badge className="bg-green-100 text-green-700 text-xs">Paid</Badge>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </DataCard>
        )}

        {/* Time History View */}
        {activeView === 'payroll' && (
          <DataCard title="Time History" subtitle={`All entries for ${getPeriodLabel()}`}>
            {timeEntries.length === 0 ? (
              <EmptyState
                icon={<History />}
                title="No time entries"
                description="Time entries will appear when team members clock in and out"
              />
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Team Member</TableHead>
                      <TableHead>Clock In</TableHead>
                      <TableHead>Clock Out</TableHead>
                      <TableHead className="text-right">Duration</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {timeEntries.map((entry) => {
                      const member = team.find(m => m.id === entry.teamMemberId);
                      const duration = entry.clockOut 
                        ? (new Date(entry.clockOut) - new Date(entry.clockIn)) / (1000 * 60)
                        : null;
                      
                      return (
                        <TableRow key={entry.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                !entry.clockOut ? 'bg-green-100' : 'bg-gray-100'
                              }`}>
                                <User className={`w-4 h-4 ${!entry.clockOut ? 'text-green-600' : 'text-gray-400'}`} />
                              </div>
                              <span className="font-medium">{member?.name || 'Unknown'}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-600">{formatDateTime(entry.clockIn)}</TableCell>
                          <TableCell className="text-gray-600">
                            {entry.clockOut ? formatDateTime(entry.clockOut) : (
                              <Badge className="bg-green-100 text-green-700 text-xs">Active</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {duration ? formatDuration(duration) : '—'}
                          </TableCell>
                          <TableCell>
                            {entry.isPaid ? (
                              <Badge className="bg-green-100 text-green-700">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Paid
                              </Badge>
                            ) : entry.clockOut ? (
                              <Badge variant="outline" className="text-amber-600 border-amber-200">
                                <DollarSign className="w-3 h-3 mr-1" />
                                Unpaid
                              </Badge>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </DataCard>
        )}
      </div>

      {/* Mark Paid Modal */}
      <Dialog open={showPayrollModal} onOpenChange={(open) => { if (!open) { setShowPayrollModal(false); setSelectedMember(null); setPayAmount(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark Hours as Paid</DialogTitle>
            <DialogDescription>
              Record payment for {selectedMember?.name}'s unpaid hours
            </DialogDescription>
          </DialogHeader>
          
          {selectedMember && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Unpaid Hours</span>
                  <span className="font-semibold">{formatDuration(calculateMemberHours(selectedMember.id).unpaidMinutes)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Unpaid Entries</span>
                  <span className="font-semibold">
                    {calculateMemberHours(selectedMember.id).entries.filter(e => !e.isPaid && e.clockOut).length}
                  </span>
                </div>
              </div>
              
              {/* Pay Amount */}
              <div>
                <Label>Payment Amount (optional)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                    {currency === 'AED' ? 'AED' : '$'}
                  </span>
                  <Input
                    type="number"
                    step="0.01"
                    value={payAmount}
                    onChange={(e) => setPayAmount(e.target.value)}
                    placeholder="0.00"
                    className="pl-12"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Enter amount paid for record keeping (optional)
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setShowPayrollModal(false); setSelectedMember(null); setPayAmount(''); }} className="h-10">
              Cancel
            </Button>
            <Button 
              onClick={handleMarkPaid} 
              disabled={markingPaid}
              className="h-10 bg-green-600 hover:bg-green-700 text-white"
            >
              {markingPaid ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              Mark as Paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TabPageLayout>
  );
};

export default TeamPayTab;
